<script lang="ts" setup>
import { datadogRum } from '@datadog/browser-rum'
import { useRouteQuery } from '@vueuse/router'

const router = useRouter()
const store = useItemsStore()
const isMobile = useIsMobile()
const itemStore = useItemStore()
const cartStore = useCartStore()
const itemsStore = useItemsStore()
const localePath = useLocalePath()
const globalStore = useGlobalStore()
const currentStore = useCurrentStore()
const categoryQuery = useRouteQuery('c')
const queryQuery = useRouteQuery('q')
const promoIdQuery = useRouteQuery('promoId')
const isLoading = ref(!!categoryQuery.value)

// @ts-ignore
itemsStore.query = queryQuery
// @ts-ignore
itemsStore.term = categoryQuery
// @ts-ignore
itemsStore.promoId = promoIdQuery

const isProductSpotlightVisible = computed(() => {
  if (globalStore.store.salesperson) {
    return false
  }

  return currentStore.storefrontPreferences?.spotlight?.enabled && store.spotlight.length > 0
})

const fetchItems = async () => {
  isLoading.value = true
  const { data } = await itemsStore.searchItems()

  if (!store.hasInitiallyLoaded) {
    store.hasInitiallyLoaded = true
  }

  store.items.push(...data)

  isLoading.value = false
}

const fetchSpotlightItems = async () => {
  isLoading.value = true
  const { data } = await itemsStore.getSpotlightItems()

  if (!data?.length) return
  store.spotlight = data.slice(0, 4)
}

const nextPage = () => {
  itemsStore.currentPage = itemsStore.currentPage + 1
  fetchItems()
}

const handleProductView = (item: IStorefrontItem) => {
  itemStore.currentItem = item
  router.push(
    localePath({
      name: 'products-id-title',
      params: {
        id: item.id,
        title: normalizeItemTitle(item.baseVariant.title ?? ''),
      },
    }),
  )
}

onBeforeRouteLeave(() => {
  store.scrollPosition = window.scrollY
})

onMounted(async () => {
  if (!isMobile.value) globalStore.config.mobileProductCellVariant = 'grid'

  window.scrollTo({
    top: store.scrollPosition,
    behavior: 'smooth',
  })

  fetchSpotlightItems()

  await nextTick()

  if (router.currentRoute.value.query.openCart === 'true') {
    setTimeout(() => {
      cartStore.open()
    }, 500)
  }
})

if (!itemsStore.items?.length && currentStore?.id && !categoryQuery.value) {
  if (itemsStore.promoId) {
    try {
      isLoading.value = true
      await itemsStore.loadPromotion()
    } catch (error) {
      datadogRum.addError(error)
    }
  }

  fetchItems()
}

onBeforeMount(() => {
  if (itemsStore.promoId) {
    itemsStore.filterByPromotion = true
  }
})
</script>

<template>
  <div
    class="relative"
    :style="{
      background: isMobile
        ? 'transparent'
        : `linear-gradient(180deg, ${currentStore.themeColorScheme?.primary}24 10vh, #f3f3f3cc 55vh, #f3f3f3 25%, #f3f3f3 100%)`,
    }"
  >
    <ClientOnly>
      <RAppStoreBanner id="store-banner" />

      <div
        class="mx-auto -mt-2 max-w-[1280px] rounded-ee-lg rounded-es-lg p-4 pt-7 sm:-mt-8 sm:px-10 sm:pb-10 sm:pt-16"
      >
        <div class="flex flex-col gap-0">
          <RProductSpotlight
            v-if="isProductSpotlightVisible"
            :title="currentStore.storefrontPreferences?.spotlight?.title"
            :show-price="currentStore.storefrontPreferences?.spotlight?.showPrices"
            :items="itemsStore.spotlight"
            @view-product="handleProductView"
          />
          <RProductFilters :class="{ 'pt-6': !isProductSpotlightVisible }" @fetch="fetchItems" />
        </div>

        <RProductZeroState v-if="itemsStore.totalRecords === 0" />

        <RSpinner v-if="isLoading && store.items.length === 0" class="h-60" />

        <RProductList v-else :is-loading="isLoading" @next-page="nextPage" />
      </div>
    </ClientOnly>
  </div>
</template>
